import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Settings,
  Gauge,
  AlertTriangle,
  Cog,
  Activity,
  ExternalLink,
  CircleDot,
  ShieldCheck,
  HelpCircle,
  BarChart3,
  TrendingUp,
  Search,
  ClipboardCheck,
  BadgeCheck,
  Users,
  Award,
  GraduationCap,
  Briefcase,
  HardHat,
  Wrench,
  MapPin,
  FileCheck,
  BookOpen,
  Zap,
  UserCheck,
  Building2
} from 'lucide-react';

const MetierForeurSondeurGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Foreur ou sondeur : quelle différence ?" },
    { id: 'section-2', title: "Le métier au quotidien" },
    { id: 'section-3', title: "La maîtrise des appareils de mesure" },
    { id: 'section-4', title: "Certifications et habilitations" },
    { id: 'section-5', title: "Se former au métier" },
    { id: 'section-6', title: "Évoluer dans le métier" },
    { id: 'section-7', title: "Questions fréquentes" },
  ];

  const faqs = [
    {
      question: "Quelle formation pour devenir foreur ou sondeur ?",
      answer: "Il n'y a pas de voie unique. On peut entrer par un CAP/Bac pro TP ou géologie, par l'alternance, ou par la formation continue avec un titre professionnel « Sondeur en géotechnique » (RNCP). Dans tous les cas, le CACES R482 B2 et l'AIPR sont des prérequis pratiquement systématiques."
    },
    {
      question: "Le CACES est-il obligatoire ?",
      answer: "La conduite d'engins de sondage et de forage est soumise à une obligation de formation à la conduite en sécurité, validée par le CACES® R482 catégorie B2. C'est la référence du métier."
    },
    {
      question: "Peut-on devenir foreur sans diplôme ?",
      answer: "Oui, l'accès par l'expérience reste courant, en démarrant comme aide-foreur. Mais les certifications réglementaires (CACES, AIPR, SST) restent indispensables, et une certification professionnelle valorise fortement le parcours."
    },
    {
      question: "Comment faire monter en compétence une équipe déjà en poste ?",
      answer: "Les formations sur mesure en entreprise sont les plus adaptées : elles se déroulent sur vos propres chantiers avec votre matériel, et peuvent cibler précisément vos besoins (instrumentation, techniques spécifiques, sécurité)."
    },
    {
      question: "Le métier recrute-t-il ?",
      answer: "Le secteur est restreint mais en tension sur les compétences techniques, avec des besoins réels en foreurs et sondeurs qualifiés, notamment sur les profils maîtrisant l'instrumentation."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Foreur ou sondeur : métier, compétences et formations | Guide complet",
    "description": "Le métier de foreur et de sondeur en géotechnique : missions, maîtrise des appareils de mesure, certifications CACES et AIPR, formations et évolution de carrière.",
    "image": "https://geots.consulting/og-images/guide-metier-foreur.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-20",
    "dateModified": "2026-07-20"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Daily missions
  const dailyMissions = [
    { mission: "Préparer le point de sondage", desc: "Implantation, accès, installation et calage de la machine, sécurisation de la zone.", icon: MapPin },
    { mission: "Réaliser le forage", desc: "Conduite de la sondeuse, choix des outils selon le terrain, gestion des tiges et du tubage.", icon: Cog },
    { mission: "Prélever des échantillons", desc: "Carottes de sol ou de roche, échantillons intacts ou remaniés, prélèvements d'eau.", icon: Target },
    { mission: "Réaliser les essais in situ", desc: "Pressiométrique, pénétrométrique, essais de perméabilité — données pour le dimensionnement.", icon: Gauge },
    { mission: "Poser des équipements", desc: "Piézomètres, tubes de captage, ancrages, instrumentation de suivi.", icon: Settings },
    { mission: "Renseigner les documents", desc: "Coupes de sondage, fiches de forage, relevés d'essais.", icon: FileText },
    { mission: "Encadrer", desc: "Le chef de sondeuse supervise aide-sondeurs, qualité des relevés et respect sécurité.", icon: Users },
  ];

  // Certifications
  const certifications = [
    { cert: "CACES® R482 catégorie B2", desc: "Conduite en sécurité des engins de forage et de sondage. La référence du métier.", essential: true },
    { cert: "AIPR (opérateur ou encadrant)", desc: "Intervention à proximité des réseaux — souvent exigée en milieu urbain et sur voirie.", essential: true },
    { cert: "SST (Sauveteur Secouriste du Travail)", desc: "Premiers secours sur chantier.", essential: true },
    { cert: "Permis B (parfois remorque ou PL)", desc: "Déplacements et transport du matériel.", essential: false },
    { cert: "Habilitations spécifiques", desc: "Selon les sites : risques chimiques, ATEX, permis feu (pétrochimie, dépollution, nucléaire).", essential: false },
  ];

  // Training paths
  const trainingPaths = [
    { 
      title: "Titre professionnel « Sondeur en géotechnique »", 
      desc: "Certification enregistrée au RNCP (RNCP41767). Structure la profession et répond aux attentes du secteur.", 
      icon: GraduationCap,
      color: "bg-blue-50 border-blue-200"
    },
    { 
      title: "Parcours certifiants", 
      desc: "Modules techniques, modules transversaux (environnement, prévention des risques, gestion d'équipe) et certifications réglementaires.", 
      icon: Award,
      color: "bg-green-50 border-green-200"
    },
    { 
      title: "Alternance et apprentissage", 
      desc: "Pratique importante sur chantier, combinée à une formation théorique.", 
      icon: Briefcase,
      color: "bg-purple-50 border-purple-200"
    },
    { 
      title: "Formations sur mesure en entreprise", 
      desc: "Dans vos locaux, sur vos chantiers, avec vos machines. Carottage, rotary destructif, tubages, micropieux, etc.", 
      icon: Building2,
      color: "bg-orange-50 border-orange-200"
    },
    { 
      title: "Formations à l'instrumentation", 
      desc: "Exploitation des enregistreurs de paramètres, pressiomètres et appareils de mesure. Le maillon souvent manquant.", 
      icon: BarChart3,
      color: "bg-cyan-50 border-cyan-200"
    },
  ];

  // Career progression
  const careerProgression = [
    { level: "Aide-foreur / Aide-sondeur", desc: "Niveau d'entrée, formation sur le terrain", icon: UserCheck },
    { level: "Foreur / Sondeur (Chef de sondeuse)", desc: "Autonomie complète, responsabilité des mesures", icon: HardHat },
    { level: "Encadrant / Chef de chantier", desc: "Supervision d'équipes, interface client", icon: Users },
    { level: "Technicien géotechnicien", desc: "Analyse, dépouillement, spécialisation", icon: BarChart3 },
  ];

  // Measurement equipment mastery
  const measurementEquipment = [
    { 
      equipment: "Enregistreur de paramètres de forage (DAC)", 
      skill: "Savoir le paramétrer, lire les valeurs en temps réel, repérer une anomalie et réagir immédiatement.",
      icon: Activity
    },
    { 
      equipment: "Pressiomètre", 
      skill: "Respecter les durées de palier, gérer la pression différentielle, reconnaître une courbe aberrante.",
      icon: Gauge
    },
    { 
      equipment: "Pénétromètre et essais in situ", 
      skill: "Protocole, conditions de validité, interprétation de premier niveau.",
      icon: TrendingUp
    },
    { 
      equipment: "Contrôle du matériel", 
      skill: "Vérifier l'étalonnage et sa validité, détecter une dérive, une fuite, une sonde fatiguée.",
      icon: ClipboardCheck
    },
    { 
      equipment: "Qualité du forage support", 
      skill: "Un essai pressiométrique dans un trou mal calibré ou aux parois remaniées est inexploitable.",
      icon: Target
    },
  ];

  return (
    <>
      <Helmet>
        <title>Foreur ou sondeur : métier, compétences et formations | Guide complet</title>
        <meta name="description" content="Le métier de foreur et de sondeur en géotechnique : missions, maîtrise des appareils de mesure, certifications CACES et AIPR, formations et évolution de carrière." />
        <link rel="canonical" href="https://geots.consulting/guides/metier-foreur-sondeur" />
        <meta property="og:title" content="Foreur ou sondeur : métier, compétences et formations | Guide complet" />
        <meta property="og:description" content="Le métier de foreur et de sondeur en géotechnique : missions, maîtrise des appareils de mesure, certifications CACES et AIPR, formations et évolution de carrière." />
        <meta property="og:url" content="https://geots.consulting/guides/metier-foreur-sondeur" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
          <div className="container mx-auto px-4 py-12 md:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8 text-gray-300">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-white">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Métier foreur sondeur</span>
            </nav>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-[#635BFF]/20 text-[#00D4FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <HardHat className="w-4 h-4" />
                Guide métier
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Foreur ou sondeur :
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D4FF] to-[#635BFF] mt-2">
                  le métier, les compétences et les formations
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
                C'est l'un des métiers les plus méconnus du BTP, et pourtant l'un des plus décisifs : sans lui, 
                aucune donnée fiable sur le sol, donc aucune fondation correctement dimensionnée. Foreur, sondeur, 
                aide-foreur, chef de sondeuse — que recouvrent réellement ces appellations, quelles compétences 
                exigent-elles, et comment se former ?
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Mis à jour : Juillet 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Temps de lecture : 12 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Sommaire
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((section, index) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Related Guides */}
                <div className="bg-gradient-to-br from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 mt-6 border border-[#635BFF]/10">
                  <h4 className="font-bold text-[#0A2540] mb-4">Guides connexes</h4>
                  <div className="space-y-3">
                    <Link to="/guides/enregistreur-parametres-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Enregistreur de paramètres
                    </Link>
                    <Link to="/guides/choisir-pressiometre-manuel-automatique" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Choisir son pressiomètre
                    </Link>
                    <Link to="/guides/materiel-de-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Matériel de forage
                    </Link>
                    <Link to="/talents" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Recrutement / Talents
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Article */}
            <article className="flex-1 max-w-3xl">
              {/* Section 1: Difference */}
              <section id="section-1" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Foreur ou sondeur : quelle différence ?
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700 mb-6">
                  <p>
                    Les deux termes circulent souvent comme des synonymes, et sur le terrain la frontière est parfois floue. 
                    On peut toutefois poser une distinction utile :
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Search className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="font-bold text-blue-800">Le sondeur</h3>
                    </div>
                    <p className="text-blue-700 text-sm mb-2">
                      Intervient dans une logique de <strong>reconnaissance</strong>. Il réalise des sondages pour 
                      <em> connaître</em> le sol : prélèvement d'échantillons, essais in situ, pose de piézomètres.
                    </p>
                    <p className="text-blue-800 font-medium text-sm">
                      Sa production : des <strong>données</strong>.
                    </p>
                  </div>

                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Cog className="w-5 h-5 text-amber-600" />
                      </div>
                      <h3 className="font-bold text-amber-800">Le foreur</h3>
                    </div>
                    <p className="text-amber-700 text-sm mb-2">
                      Désigne plus largement celui qui <strong>réalise un forage</strong>, quel qu'en soit l'objectif : 
                      fondations (pieux, micropieux), recherche d'eau, géothermie, ancrages, travaux souterrains.
                    </p>
                    <p className="text-amber-800 font-medium text-sm">
                      Sa production : un <strong>ouvrage</strong>.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border">
                  <p className="text-gray-700 mb-3">
                    En pratique, beaucoup de professionnels exercent les deux facettes, et les intitulés varient selon les entreprises. 
                    Le point commun : la <strong>maîtrise d'une machine de forage</strong> et la <strong>responsabilité</strong> de ce qui 
                    se passe au point de sondage.
                  </p>
                  <div className="bg-white rounded-lg p-4 border">
                    <p className="text-sm text-gray-600">
                      <strong>Le secteur en France :</strong> environ <span className="text-[#635BFF] font-semibold">300 entreprises</span> et 
                      <span className="text-[#635BFF] font-semibold"> 100 indépendants</span>, pour un chiffre d'affaires de l'ordre de 
                      <span className="text-[#635BFF] font-semibold"> 780 millions d'euros</span>. Un secteur restreint, technique, et en tension sur les compétences.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 2: Daily work */}
              <section id="section-2" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-green-600" />
                  </span>
                  Le métier au quotidien
                </h2>

                <p className="text-gray-700 mb-6">
                  Les missions varient selon les chantiers, mais on retrouve un socle commun :
                </p>

                <div className="space-y-3 mb-6">
                  {dailyMissions.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-4 border hover:border-[#635BFF]/50 hover:shadow-md transition-all flex items-start gap-4"
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 text-[#635BFF]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-1">{item.mission}</h3>
                          <p className="text-gray-600 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#635BFF]">
                  <p className="text-gray-700">
                    Le métier s'exerce en <strong>extérieur</strong>, avec des déplacements fréquents et des horaires variables. 
                    Il demande de l'<strong>autonomie</strong>, de la <strong>rigueur</strong> et une <strong>bonne condition physique</strong>.
                  </p>
                </div>
              </section>

              {/* Section 3: Measurement mastery */}
              <section id="section-3" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-red-600" />
                  </span>
                  🎯 La maîtrise des appareils de mesure : le cœur du métier
                </h2>

                <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-red-800 font-medium mb-2">
                        C'est le point le plus sous-estimé, et pourtant celui qui distingue un bon foreur d'un simple conducteur d'engin.
                      </p>
                      <p className="text-red-700">
                        <strong>Un sondeur ne conduit pas une machine : il produit une mesure.</strong> Et cette mesure servira à dimensionner 
                        des fondations, à valider un ouvrage, parfois à trancher un litige. Une donnée fausse, et c'est toute la chaîne qui 
                        est faussée — jusqu'au calcul de portance d'un bâtiment.
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-gray-700 mb-4">Concrètement, cela suppose de maîtriser :</p>

                <div className="space-y-4 mb-6">
                  {measurementEquipment.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-5 border shadow-sm"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-2">{item.equipment}</h3>
                            <p className="text-gray-600 text-sm">{item.skill}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#00D4FF]">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-[#00D4FF] flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-gray-700">
                        👉 C'est pourquoi la <strong>montée en compétence sur l'instrumentation</strong> est aujourd'hui un enjeu majeur 
                        pour les entreprises de sondage : elle conditionne la fiabilité de leurs livrables et leur crédibilité auprès des donneurs d'ordre.
                      </p>
                      <Link 
                        to="/guides/enregistreur-parametres-forage"
                        className="inline-flex items-center gap-2 mt-3 text-[#635BFF] font-medium hover:underline"
                      >
                        Guide enregistreur de paramètres <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4: Certifications */}
              <section id="section-4" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <BadgeCheck className="w-5 h-5 text-purple-600" />
                  </span>
                  Les certifications et habilitations
                </h2>

                <p className="text-gray-700 mb-6">Certaines sont incontournables pour exercer :</p>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                    <thead>
                      <tr className="bg-[#0A2540] text-white">
                        <th className="text-left p-4 font-semibold">Certification</th>
                        <th className="text-left p-4 font-semibold">Objet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certifications.map((row, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="p-4">
                            <span className={`font-medium ${row.essential ? 'text-[#635BFF]' : 'text-[#0A2540]'}`}>
                              {row.cert}
                            </span>
                            {row.essential && (
                              <span className="ml-2 text-xs bg-[#635BFF]/10 text-[#635BFF] px-2 py-0.5 rounded">Essentiel</span>
                            )}
                          </td>
                          <td className="p-4 text-gray-600 text-sm">{row.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border">
                  <p className="text-gray-600 text-sm">
                    À cela s'ajoutent les formations liées au <strong>travail en hauteur</strong>, à la <strong>manutention</strong> 
                    et aux <strong>gestes et postures</strong> selon les configurations de chantier.
                  </p>
                </div>
              </section>

              {/* Section 5: Training */}
              <section id="section-5" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                  </span>
                  Se former au métier : les voies possibles
                </h2>

                <p className="text-gray-700 mb-6">
                  Le métier s'apprend beaucoup <strong>sur le terrain</strong>, mais les parcours structurés se sont développés ces dernières années.
                </p>

                <div className="space-y-4 mb-6">
                  {trainingPaths.map((path, index) => {
                    const IconComponent = path.icon;
                    return (
                      <div 
                        key={index}
                        className={`rounded-xl p-5 border-2 ${path.color}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                            <IconComponent className="w-6 h-6 text-[#0A2540]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-2">{path.title}</h3>
                            <p className="text-gray-600 text-sm">{path.desc}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 6: Career evolution */}
              <section id="section-6" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </span>
                  Évoluer dans le métier
                </h2>

                <p className="text-gray-700 mb-6">Le parcours typique suit une progression claire :</p>

                <div className="relative mb-6">
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#635BFF] to-[#00D4FF]"></div>
                  <div className="space-y-4">
                    {careerProgression.map((level, index) => {
                      const IconComponent = level.icon;
                      return (
                        <div key={index} className="flex items-start gap-4 ml-0">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center flex-shrink-0 z-10 border-4 border-white shadow-lg">
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          <div className="bg-white rounded-xl p-4 border shadow-sm flex-1">
                            <h3 className="font-bold text-[#0A2540] mb-1">{level.level}</h3>
                            <p className="text-gray-600 text-sm">{level.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border">
                  <p className="text-gray-700 mb-3">
                    Les niveaux d'entrée vont du <strong>CAP/Bac pro travaux publics ou géologie</strong> jusqu'au 
                    <strong> Bac+2/+3</strong> pour les fonctions techniques et d'encadrement.
                  </p>
                  <p className="text-gray-700">
                    La <strong>spécialisation</strong> (micropieux, géothermie, environnement, instrumentation) est un levier 
                    de progression important — et souvent de rémunération.
                  </p>
                </div>
              </section>

              {/* Section 7: FAQ */}
              <section id="section-7" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-indigo-600" />
                  </span>
                  Questions fréquentes
                </h2>

                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-[#0A2540] pr-4">{faq.question}</span>
                        {openFaq === index ? (
                          <ChevronUp className="w-5 h-5 text-[#635BFF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-5 text-gray-600 border-t">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* CTA Box */}
              <div className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-2xl p-8 text-white mb-8">
                <h3 className="text-xl font-bold mb-4">
                  Se former, s'équiper, être mis en relation
                </h3>
                <p className="text-gray-300 mb-4">
                  Que vous cherchiez à entrer dans le métier, à faire monter en compétence vos équipes ou à recruter des profils qualifiés, 
                  la difficulté est souvent la même : <strong>trouver le bon interlocuteur</strong>.
                </p>
                <p className="text-gray-300 mb-6">
                  GEOTS n'est pas un organisme de formation, mais met en relation les acteurs de la géotechnique avec les meilleurs 
                  organismes et formateurs spécialisés du secteur. GEOTS met également en relation entreprises, foreurs et sondeurs 
                  pour le recrutement et les missions.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5248d9] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Contactez-nous
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link 
                    to="/talents"
                    className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Voir les talents / recrutement
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="text-sm text-gray-500 italic border-t pt-8">
                Ce guide est fourni à titre informatif. Les obligations de formation et de certification relèvent de la réglementation 
                en vigueur et doivent être vérifiées auprès des organismes compétents.
              </div>
            </article>
          </div>
        </div>

        {/* Related Guides Section */}
        <div className="bg-gray-50 border-t py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-8 text-center">
              Continuez votre lecture
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link 
                to="/guides/enregistreur-parametres-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Enregistreur de paramètres de forage
                </h3>
                <p className="text-gray-600 text-sm">
                  DAC, contrôle qualité et traçabilité.
                </p>
              </Link>

              <Link 
                to="/guides/choisir-pressiometre-manuel-automatique"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center mb-4">
                  <Gauge className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Choisir son pressiomètre
                </h3>
                <p className="text-gray-600 text-sm">
                  Manuel ou automatique, neuf ou occasion.
                </p>
              </Link>

              <Link 
                to="/guides/materiel-de-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Matériel de forage : guide complet
                </h3>
                <p className="text-gray-600 text-sm">
                  Foreuses, outils et instrumentation.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MetierForeurSondeurGuide;
